import React, { useCallback, useMemo, useState } from 'react';
import { Card, Col, Row, Statistic, Dropdown, Divider, Typography, Space, Badge } from 'antd';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { Formatter } from '../utils';
import FaIcon from './FaIcon';

interface StatsBoxProps {
  data: any;
  kpi?: 'Standard' | 'SKAN' | 'Combined';
}

const metricColStyle: React.CSSProperties = {
  padding: '5px',
  textAlign: 'center',
  flex: '0 0 auto',
  minWidth: 130,
  whiteSpace: 'nowrap',
};

const metricMmpColStyle: React.CSSProperties = {
  ...metricColStyle,
  minWidth: 180,
};

function MmpCard(data: any) {
  return (
    <Row>
      <Col span={8}>
        <Statistic
          value={Formatter.count(data.ct)}
          valueStyle={{
            fontSize: 10,
            fontWeight: 'bold',
            color: data.color,
          }}
        />
      </Col>
      <Col span={8}>
        <Statistic
          value={Formatter.count(data.vt)}
          valueStyle={{
            fontSize: 10,
            fontWeight: 'bold',
            color: data.color,
          }}
        />
      </Col>
      <Col span={8}>
        <Statistic
          value={data.total}
          valueStyle={{
            fontSize: 10,
            fontWeight: 'bold',
            color: data.color,
          }}
        />
      </Col>
    </Row>
  );
}

export default function StatsBox(props: StatsBoxProps) {
  const { data, kpi: kpiProp } = props;
  const [kpi, setKpi] = useState(1);
  const handleKpiSelection = useCallback((key: number) => {
    setKpi(key);
  }, []);

  const dropdownItems = useMemo(
    () => [
      {
        key: 1,
        label: 'Standard',
        onClick: ({ key }: { key: number }) => {
          return handleKpiSelection(key);
        },
      },
      {
        key: 2,
        label: 'SKAN',
        onClick: ({ key }: { key: number }) => handleKpiSelection(key),
      },
      {
        key: 3,
        label: 'Combined',
        onClick: ({ key }: { key: number }) => handleKpiSelection(key),
      },
    ],
    [handleKpiSelection],
  );
  const getKpiName = useCallback(
    () =>
      dropdownItems?.find((v) => {
        return v?.key == kpi;
      })?.label,
    [dropdownItems, kpi],
  );
  return (
    <>
      <Space
        size="large"
        direction="horizontal"
        style={{ height: 30, padding: 5, marginBottom: 12 }}
      >
        <Badge color="orange" text="MMP" />
        <Badge color="green" text="Self Attributed" />
        <Badge color="cyan" text="Assisted" />
      </Space>
      <Card
        size="small"
        title={
          !kpiProp ? (
            <Dropdown
              // @ts-ignore
              menu={{ items: dropdownItems }}
              trigger={['click']}
            >
              <Space align="center" style={{ cursor: 'pointer' }}>
                <Typography.Text
                  style={{ fontSize: 14 }}
                >{`${getKpiName()} KPI Overview`}</Typography.Text>
                <FaIcon icon={faCaretDown} />
              </Space>
            </Dropdown>
          ) : (
            <Typography.Text style={{ fontSize: 14 }}>{`${kpiProp} KPI Overview`}</Typography.Text>
          )
        }
      >
        <Row align="middle" gutter={0} style={{ flexFlow: 'nowrap', overflowX: 'auto' }}>
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Total Impressions
                </Typography.Text>
              }
              value={Formatter.count(data.total_impressions)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Impressions
                </Typography.Text>
              }
              value={Formatter.count(data.impressions)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Viewable Impression
                </Typography.Text>
              }
              value={Formatter.count(data.viewable_impression)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Total Clicks
                </Typography.Text>
              }
              value={Formatter.count(data.total_clicks)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Clicks
                </Typography.Text>
              }
              value={Formatter.count(data.clicks)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Win Rate
                </Typography.Text>
              }
              value={Formatter.count(data.win_rate, 4)}
              suffix="%"
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={3} style={metricMmpColStyle}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Installs
            </Typography.Text>
            <Row>
              <Col span={8}>
                <Typography.Text strong type="secondary" style={{ fontSize: 11 }}>
                  CT
                </Typography.Text>
              </Col>
              <Col span={8}>
                <Typography.Text strong type="secondary" style={{ fontSize: 11 }}>
                  VT
                </Typography.Text>
              </Col>
              <Col span={8}>
                <Typography.Text strong type="secondary" style={{ fontSize: 11 }}>
                  Total
                </Typography.Text>
              </Col>
            </Row>
            {<MmpCard color="orange" ct={data.CT_MMP} vt={data.VT_MMP} total={data.TOTAL_MMP} />}
            {<MmpCard color="green" ct={data.CT} vt={data.VT} total={data.TOTAL} />}
            {<MmpCard color="#01dbc9" ct={data.CT_A} vt={data.VT_A} total={data.TOTAL_A} />}
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Conversions
                </Typography.Text>
              }
              value={Formatter.count(data.conversions)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  CTR
                </Typography.Text>
              }
              value={Formatter.count(data.CTR, 4)}
              suffix="%"
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  CT CR
                </Typography.Text>
              }
              value={Formatter.count(data.CT_CR, 4)}
              suffix="%"
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  IPM
                </Typography.Text>
              }
              value={Formatter.count(data.IPM)}
              valueStyle={{
                fontSize: 15,
              }}
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  eCPM
                </Typography.Text>
              }
              value={Formatter.count(data.eCPM, 4)}
              valueStyle={{
                fontSize: 15,
              }}
              prefix="$"
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  eCPC
                </Typography.Text>
              }
              value={Formatter.count(data.eCPC, 4)}
              valueStyle={{
                fontSize: 15,
              }}
              prefix="$"
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  eCPI
                </Typography.Text>
              }
              value={Formatter.count(data.eCPI, 4)}
              valueStyle={{
                fontSize: 15,
              }}
              prefix="$"
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  eCPA
                </Typography.Text>
              }
              value={Formatter.count(data.eCPA, 4)}
              valueStyle={{
                fontSize: 15,
              }}
              prefix="$"
            />
          </Col>
          <Divider type="vertical" style={{ fontSize: '112px' }} />
          <Col span={2} style={metricColStyle}>
            <Statistic
              title={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Spend
                </Typography.Text>
              }
              value={Formatter.count(data.spend, 4)}
              valueStyle={{
                fontSize: 15,
              }}
              prefix="$"
            />
          </Col>
        </Row>
      </Card>
    </>
  );
}
